import React from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge, type StatusBadgeVariant } from '@hofsync/design-system';
import { useIntegrationAgentImports } from '../../hooks/useIntegrationAgentImports';
import type { AgentImport } from '../../services/integrationAgentService';
import './IntegrationAgentImports.css';
import './tableScroll.css';

/** Map a document ingest status to the shared StatusBadge variant. */
function statusVariant(status: string | null): StatusBadgeVariant {
  if (status === 'completed') return 'connected';
  if (status === 'failed') return 'error';
  // processing / queued / pending / unknown all read as "in Arbeit".
  return 'connecting';
}

/** Total currently-linked objects + a compact per-type breakdown, or null. */
function linkedSummary(counts: AgentImport['entity_counts']): { total: number; breakdown: string } | null {
  if (!counts) return null;
  const entries = Object.entries(counts);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const breakdown = entries.map(([type, n]) => `${type}: ${n}`).join(', ');
  return { total, breakdown };
}

function formatUploadedAt(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

/**
 * IntegrationAgentImports (#1936) — observed-imports history for the browser-
 * extension backfill. Per Erntejahr: the workbook, upload time, ingest status,
 * and currently-linked twin objects. Honest single empty state — the server
 * reports only what actually landed, so "no imports yet" never implies a
 * backfill ran. Built on DS primitives (StatusBadge) per #1939.
 */
const IntegrationAgentImports: React.FC<{ source?: string }> = ({ source = 'myfarm24' }) => {
  // Generated, not hardcoded: this component is parameterised by `source`, so
  // one section per connected portal is the obvious next step and two instances
  // would otherwise emit the same id (#3480 review round 3).
  const headingId = `agent-imports-title-${React.useId()}`;
  const { t } = useTranslation();
  const { data, isLoading, error } = useIntegrationAgentImports(source);

  const statusText = (status: string | null): string => {
    if (status === 'completed') return t('integrations.agent.status.completed', 'Verarbeitet');
    if (status === 'failed') return t('integrations.agent.status.failed', 'Fehlgeschlagen');
    return t('integrations.agent.status.processing', 'In Arbeit');
  };

  const imports = data?.imports ?? [];

  return (
    <section className="agent-imports" aria-labelledby={headingId}>
      <div className="agent-imports__header">
        <h2 id={headingId} className="agent-imports__title">
          {t('integrations.agent.title', 'Portal-Importe')}
        </h2>
        <p className="agent-imports__description">
          {t('integrations.agent.description', 'Automatisch importierte Erntejahre aus verbundenen Portalen.')}
        </p>
      </div>

      {isLoading && (
        <div className="agent-imports__spinner" role="status" aria-label={t('integrations.agent.loading', 'Wird geladen…')} />
      )}

      {!isLoading && error && (
        <p className="agent-imports__error">
          {t('integrations.agent.error', 'Importverlauf konnte nicht geladen werden.')}
        </p>
      )}

      {!isLoading && !error && imports.length === 0 && (
        <p className="agent-imports__empty">
          {t('integrations.agent.empty', 'Noch keine importierten Jahre.')}
        </p>
      )}

      {!isLoading && !error && imports.length > 0 && (
        <div
          className="integrations-table-scroll"
          role="region"
          aria-label={t('integrations.agent.tableLabel', 'Importierte Jahre — Tabelle')}
          tabIndex={0}
        >
          <table className="agent-imports__table">
            <thead>
              <tr>
                <th scope="col">{t('integrations.agent.columns.year', 'Jahr')}</th>
                <th scope="col">{t('integrations.agent.columns.document', 'Dokument')}</th>
                <th scope="col">{t('integrations.agent.columns.uploadedAt', 'Hochgeladen')}</th>
                <th scope="col">{t('integrations.agent.columns.status', 'Status')}</th>
                <th scope="col">{t('integrations.agent.columns.linkedObjects', 'Verknüpfte Objekte (aktuell)')}</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((imp, idx) => {
                const summary = linkedSummary(imp.entity_counts);
                return (
                  <tr className="agent-imports__row" key={imp.doc_id ?? `${imp.year ?? 'unknown'}-${idx}`}>
                    <td className="agent-imports__year">
                      {imp.year ?? t('integrations.agent.unknownYear', 'Unbekannt')}
                    </td>
                    <td className="agent-imports__document">{imp.filename ?? '—'}</td>
                    <td className="agent-imports__uploaded">{formatUploadedAt(imp.uploaded_at)}</td>
                    <td className="agent-imports__status">
                      <StatusBadge variant={statusVariant(imp.status)} text={statusText(imp.status)} />
                    </td>
                    <td className="agent-imports__linked" title={summary?.breakdown || undefined}>
                      {summary ? summary.total : t('integrations.agent.linkedObjectsUnavailable', '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default IntegrationAgentImports;
